/**
 * Shared utilities for school-related components
 */

/**
 * Format a grade code to human-readable format.
 *
 * Valid codes are 'PK' (Pre-K), 'KG'/'K' (Kindergarten), and grades 1-12 (accepted
 * either zero-padded like '01' or unpadded like '1'). Anything else - including
 * out-of-range values like '17' that can appear from bad school import data - is
 * treated as unknown ('?') rather than rendered verbatim, so the UI never shows a
 * nonsense grade.
 *
 * @param {string} grade - Grade code (e.g., 'PK', 'KG', '01', '12')
 * @returns {string} Formatted grade, or '?' if missing/unrecognized
 */
const formatGrade = (grade) => {
  if (!grade) return '?'
  const normalized = String(grade).trim().toUpperCase()
  if (normalized === 'PK') return 'Pre-K'
  if (normalized === 'KG' || normalized === 'K') return 'K'
  if (/^\d{1,2}$/.test(normalized)) {
    const n = parseInt(normalized, 10)
    if (n >= 1 && n <= 12) return String(n)
  }
  return '?'
}

/**
 * Format grade range for display
 * @param {string} lowest - Lowest grade code
 * @param {string} highest - Highest grade code
 * @param {Object} options - Formatting options
 * @param {boolean} options.includePrefix - Include 'Grades ' prefix (default: false)
 * @param {string} options.emptyValue - Value to return if both grades are empty (default: '')
 * @returns {string} Formatted grade range
 */
export const formatGradeRange = (lowest, highest, options = {}) => {
  const { includePrefix = false, emptyValue = '' } = options
  if (!lowest && !highest) return emptyValue
  const prefix = includePrefix ? 'Grades ' : ''

  const low = formatGrade(lowest)
  const high = formatGrade(highest)

  // Drop unknown/out-of-range codes so bad data can't render as a nonsense range
  // (e.g. a school record with gradeHighest '17' must not display "2 - 17").
  const valid = [low, high].filter((grade) => grade !== '?')
  if (valid.length === 0) return emptyValue
  if (valid.length === 1) return `${prefix}${valid[0]}`
  if (low === high) return `${prefix}${low}`
  return `${prefix}${low} - ${high}`
}

/**
 * Get school type info for badge display
 * @param {Object} school - School object
 * @returns {Object} Badge info with label and style classes
 */
export const getSchoolTypeInfo = (school) => {
  if (school.isCharter) {
    return {
      label: 'Charter',
      bgClass: 'bg-purple-100 dark:bg-purple-900/30',
      textClass: 'text-purple-800 dark:text-purple-300',
      hasIcon: true,
      iconType: 'sparkles'
    }
  }
  if (school.schoolType === 'PRIVATE') {
    return {
      label: 'Private',
      bgClass: 'bg-amber-100 dark:bg-amber-900/30',
      textClass: 'text-amber-700 dark:text-amber-300',
      hasIcon: false
    }
  }
  return {
    label: 'Public',
    bgClass: 'bg-blue-100 dark:bg-blue-900/30',
    textClass: 'text-blue-700 dark:text-blue-300',
    hasIcon: false
  }
}
